import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, ChevronUp, X } from 'lucide-react';
import type { EmCatalogEntry } from '../../types/codingAssistant';
import { fetchEMCodeCatalog } from '../../services/codingAssistant';

interface Props {
  onPick: (code: string) => void;
  onCancel: () => void;
  isBusy: boolean;
}

const MDM_COLOR: Record<string, string> = {
  high: 'bg-red-100 text-red-800',
  moderate: 'bg-orange-100 text-orange-800',
  low: 'bg-yellow-100 text-yellow-800',
  straightforward: 'bg-blue-100 text-blue-800',
  minimal: 'bg-gray-100 text-gray-800',
};

export const EMCodeCatalogPicker: React.FC<Props> = ({ onPick, onCancel, isBusy }) => {
  const [codes, setCodes] = useState<EmCatalogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await fetchEMCodeCatalog();
        if (!cancelled) {
          setCodes(response.data.codes);
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(err?.response?.data?.error || 'Failed to load E&M code catalog');
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const groups = useMemo(() => {
    const newPt = codes.filter((c) => c.patientType === 'new');
    const established = codes.filter((c) => c.patientType === 'established');
    const other = codes.filter((c) => c.patientType !== 'new' && c.patientType !== 'established');
    return { newPt, established, other };
  }, [codes]);

  return (
    <div className="border border-gray-200 bg-white rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Pick an E&M code manually</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Select a code from the catalog to override the AI suggestion.
          </p>
        </div>
        <button
          onClick={onCancel}
          disabled={isBusy}
          className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors disabled:opacity-50"
          title="Cancel"
          aria-label="Cancel"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
        </div>
      )}

      {!isLoading && error && (
        <div className="border border-red-200 bg-red-50 rounded-md p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {!isLoading && !error && codes.length === 0 && (
        <div className="text-center py-6 text-sm text-gray-500">No E&M codes available.</div>
      )}

      {!isLoading && !error && codes.length > 0 && (
        <div className="space-y-4 max-h-[60vh] overflow-y-auto">
          {groups.newPt.length > 0 && (
            <CatalogGroup
              label="New patient"
              tone="new"
              entries={groups.newPt}
              onPick={onPick}
              isBusy={isBusy}
            />
          )}
          {groups.established.length > 0 && (
            <CatalogGroup
              label="Established patient"
              tone="established"
              entries={groups.established}
              onPick={onPick}
              isBusy={isBusy}
            />
          )}
          {groups.other.length > 0 && (
            <CatalogGroup
              label="Other"
              tone="other"
              entries={groups.other}
              onPick={onPick}
              isBusy={isBusy}
            />
          )}
        </div>
      )}

      <div className="flex justify-end pt-1">
        <button
          onClick={onCancel}
          disabled={isBusy}
          className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

type GroupTone = 'new' | 'established' | 'other';

interface CatalogGroupProps {
  label: string;
  tone: GroupTone;
  entries: EmCatalogEntry[];
  onPick: (code: string) => void;
  isBusy: boolean;
}

const GROUP_TONE: Record<GroupTone, { label: string; container: string; row: string; divide: string }> = {
  new: {
    label: 'bg-emerald-100 text-emerald-800',
    container: 'border-emerald-200',
    row: 'bg-emerald-50/40 hover:bg-emerald-50',
    divide: 'divide-emerald-100',
  },
  established: {
    label: 'bg-indigo-100 text-indigo-800',
    container: 'border-indigo-200',
    row: 'bg-indigo-50/40 hover:bg-indigo-50',
    divide: 'divide-indigo-100',
  },
  other: {
    label: 'bg-gray-100 text-gray-600',
    container: 'border-gray-200',
    row: 'bg-white hover:bg-gray-50',
    divide: 'divide-gray-200',
  },
};

const CatalogGroup: React.FC<CatalogGroupProps> = ({ label, tone, entries, onPick, isBusy }) => {
  const toneStyle = GROUP_TONE[tone];
  return (
    <div>
      <span className={`inline-block mb-2 px-2 py-0.5 rounded text-xs font-semibold tracking-wide uppercase ${toneStyle.label}`}>
        {label}
      </span>
      <div className={`border rounded-md divide-y ${toneStyle.container} ${toneStyle.divide}`}>
        {entries.map((entry) => (
          <CatalogRow
            key={entry.code}
            entry={entry}
            rowClass={toneStyle.row}
            onPick={onPick}
            isBusy={isBusy}
          />
        ))}
      </div>
    </div>
  );
};

interface CatalogRowProps {
  entry: EmCatalogEntry;
  rowClass: string;
  onPick: (code: string) => void;
  isBusy: boolean;
}

const CatalogRow: React.FC<CatalogRowProps> = ({ entry, rowClass, onPick, isBusy }) => {
  const [expanded, setExpanded] = useState(false);
  const mdmKey = entry.mdmLevel ?? '';
  const mdmColor = MDM_COLOR[mdmKey] ?? 'bg-gray-100 text-gray-800';
  const mdmLabel = entry.mdmLevel ? `${entry.mdmLevel.toUpperCase()} MDM` : null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className={`transition-colors ${rowClass}`}
    >
      <div className="flex items-start gap-3 p-3">
        <button
          onClick={() => onPick(entry.code)}
          disabled={isBusy}
          className="flex-1 text-left flex items-start gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span className="font-mono font-bold text-sm text-gray-900 w-14 shrink-0">
            {entry.code}
          </span>
          <div className="flex-1 min-w-0">
            <p className={expanded ? 'text-sm text-gray-700' : 'text-sm text-gray-700 line-clamp-1'}>
              {entry.description}
            </p>
          </div>
          {mdmLabel && (
            <span className={`px-1.5 py-0.5 rounded text-xs font-medium shrink-0 ${mdmColor}`}>
              {mdmLabel}
            </span>
          )}
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((v) => !v);
          }}
          className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded shrink-0"
          title={expanded ? 'Collapse description' : 'Expand description'}
          aria-label={expanded ? 'Collapse description' : 'Expand description'}
        >
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>
    </motion.div>
  );
};
